import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { AlertTriangle, Truck, Scale, CheckCircle2 } from "lucide-react";
import { money, fechaHoraMX } from "@/lib/format";

const ESTADO = {
  pendiente: { t: "Incidencia pendiente", cls: "border-red-300 bg-red-50" },
  segundo_viaje: { t: "Esperando segundo viaje", cls: "border-amber-300 bg-amber-50" },
  resuelta: { t: "Incidencia resuelta", cls: "border-emerald-300 bg-emerald-50" },
};

export default function IncidenciaPanel({ pedido, onChange }) {
  const inc = pedido.incidencia;
  const [accion, setAccion] = useState(null);
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  if (!inc) return null;
  const monto = (inc.items || []).reduce((a, i) => a + Number(i.importe || 0), 0);
  const res = inc.resolucion;
  const ejecutar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pedidos/${pedido.id}/incidencia/${accion}`, { nota: nota || null });
      onChange(data);
      toast.success(accion === "ajuste" ? "Pedido cerrado con ajuste" : `Remisión de complemento ${data.incidencia?.resolucion?.remision_complemento} creada en Pedidos por surtir`);
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setBusy(false); setAccion(null); }
  };
  const e = ESTADO[inc.estado] || ESTADO.pendiente;
  return (
    <div className={`md:col-span-2 rounded-lg border p-3 space-y-2 ${e.cls}`} data-testid="incidencia-panel">
      <div className="flex items-center gap-2 font-semibold text-sm">{inc.estado === "resuelta" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-red-600" />}<span data-testid="incidencia-estado">{e.t}</span></div>
      <p className="text-xs text-muted-foreground">Reportada por {inc.chofer} el {fechaHoraMX(inc.reportada_at)} · remisión {inc.remision}</p>
      <div className="rounded border bg-white overflow-x-auto">
        <table className="w-full text-xs"><thead><tr className="bg-muted/50"><th className="p-1.5 text-left">Problema</th><th className="p-1.5 text-left">Modelo</th><th className="p-1.5 text-right">Atados</th><th className="p-1.5 text-right">Kg</th><th className="p-1.5 text-right">Importe</th></tr></thead>
          <tbody>{(inc.items || []).map((i, k) => (
            <tr key={k} className="border-t" data-testid={`incidencia-fila-${k}`}><td className="p-1.5">{i.tipo}</td><td className="p-1.5">{i.modelo}</td><td className="p-1.5 text-right">{i.atados}</td><td className="p-1.5 text-right">{i.kg}</td><td className="p-1.5 text-right">{money(i.importe)}</td></tr>
          ))}</tbody></table>
      </div>
      {inc.nota && <p className="text-xs"><b>Nota del cliente:</b> {inc.nota}</p>}
      {inc.estado === "pendiente" && (
        <>
          <p className="text-xs text-red-700">La facturación está bloqueada hasta resolver. Habla con el cliente y elige:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={() => setAccion("segundo-viaje")} data-testid="incidencia-segundo-viaje"><Truck className="h-4 w-4 mr-1" />Corregir con segundo viaje</Button>
            <Button type="button" variant="outline" onClick={() => setAccion("ajuste")} data-testid="incidencia-ajuste"><Scale className="h-4 w-4 mr-1" />Cerrar con ajuste (−{money(monto)})</Button>
          </div>
        </>
      )}
      {inc.estado === "segundo_viaje" && <p className="text-xs" data-testid="incidencia-esperando">Remisión de complemento <b>{res?.remision_complemento}</b> en Pedidos por surtir. Al entregarla el chofer, la incidencia se cierra sola y será la remisión definitiva.</p>}
      {inc.estado === "resuelta" && res?.tipo === "ajuste" && <p className="text-xs" data-testid="incidencia-resuelta-ajuste">Cerrado con ajuste de −{money(res.monto_ajuste)}: subtotal {money(res.subtotal_antes)} → {money(res.subtotal_despues)}. {res.kg_devuelto} kg regresaron al inventario.</p>}
      {inc.estado === "resuelta" && res?.tipo === "segundo_viaje" && <p className="text-xs" data-testid="incidencia-resuelta-viaje">Segundo viaje entregado. Remisión definitiva: <b>{inc.remision_definitiva}</b>. Envíala al vendedor con el botón de remisión.</p>}
      <AlertDialog open={!!accion} onOpenChange={(o) => !o && setAccion(null)}>
        <AlertDialogContent data-testid="incidencia-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{accion === "ajuste" ? `¿Cerrar con ajuste de −${money(monto)}?` : "¿Crear segundo viaje?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {accion === "ajuste"
                ? `El subtotal baja de ${money(pedido.subtotal)} a ${money(Math.max(0, Number(pedido.subtotal || 0) - monto))}; se recalcula la comisión y los atados regresan al inventario. La cotización no se modifica.`
                : "Se crea una remisión de complemento con lo afectado en Pedidos por surtir; los atados afectados regresan al inventario y el monto del pedido no cambia."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div><Label>Nota (opcional)</Label><Textarea rows={2} value={nota} onChange={(ev) => setNota(ev.target.value)} placeholder="Ej. El cliente pidió que se le reponga" data-testid="incidencia-resolver-nota" /></div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="incidencia-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(ev) => { ev.preventDefault(); ejecutar(); }} disabled={busy} data-testid="incidencia-confirmar">{busy ? "Guardando..." : "Confirmar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
